// src/RealTimeNotifications.js - the bell: what still needs attention
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Badge,
  Menu,
  Divider,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import useAdminNotifications from './useAdminNotifications';

import { brand, surface } from './designTokens';

export default function RealTimeNotifications() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { unread, unreadCount, markAsRead, markAllAsRead, refresh, addLive } =
    useAdminNotifications();

  // The bell lists what is still outstanding, not a history. Clearing empties
  // it, which is what "clear" has to mean for the word to be worth having —
  // marking everything read and then still showing it greyed out would leave
  // Samantha looking at the same list she just dealt with. The underlying
  // payout, ticket or report is untouched and stays on its own page.
  useEffect(() => {
    const bookingsChannel = supabase
      .channel('bookings-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bookings' },
        (payload) => {
          addLive({
            // Same "<source>:<id>" shape the fetch builds, or a live arrival
            // would be a second copy of a row already listed and would not
            // match its saved read mark.
            id: `booking:${payload.new.id}`,
            type: 'info',
            title: 'New Booking Request',
            message: 'New booking request created',
            timestamp: payload.new.created_at,
            read: false,
            action: '/dashboard/bookings',
          });
        },
      )
      .subscribe();

    const payoutsChannel = supabase
      .channel('payouts-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'payout_requests' },
        (payload) => {
          addLive({
            id: `payout:${payload.new.id}`,
            type: 'info',
            title: 'New Payout Request',
            message: `Payout request of $${payload.new.amount || payload.new.total_amount} created`,
            timestamp: payload.new.created_at,
            read: false,
            action: '/dashboard/payouts',
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(bookingsChannel);
      supabase.removeChannel(payoutsChannel);
    };
  }, [addLive]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Catch anything that arrived while the page sat open.
    refresh();
  };

  const handleClose = () => setAnchorEl(null);

  // Go where the thing actually is, and mark it seen on the way. Opening the
  // payouts page is dealing with it; making the admin also click a tick is
  // asking them to tell the app something it just watched them do.
  const handleOpenItem = async (notification) => {
    handleClose();
    await markAsRead(notification.id);
    if (notification.action) navigate(notification.action);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{ color: '#666', '&:hover': { color: '#333' }, position: 'relative' }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: 400, maxHeight: 600, mt: 1.5 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllAsRead} sx={{ textTransform: 'none' }}>
              Clear all
            </Button>
          )}
        </Box>
        <Divider />
        <List sx={{ maxHeight: 500, overflow: 'auto' }}>
          {unread.length === 0 ? (
            <ListItem>
              <ListItemText primary="Nothing waiting" secondary="You're all caught up." />
            </ListItem>
          ) : (
            unread.map((notification) => (
              <ListItem
                key={notification.id}
                onClick={() => handleOpenItem(notification)}
                sx={{
                  bgcolor: brand.goldWash,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: surface.page },
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: `${getColor(notification.type)}.light` }}>
                    {getIcon(notification.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(notification.timestamp).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
                <IconButton
                  size="small"
                  aria-label="Clear"
                  onClick={(e) => {
                    // Clearing one without leaving the page or opening it.
                    e.stopPropagation();
                    markAsRead(notification.id);
                  }}
                  sx={{ ml: 1 }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))
          )}
        </List>
      </Menu>
    </>
  );
}
